import { useEffect, useMemo, useState } from 'react';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5006';

/** Cores por tipo de cargo — espelham o PDF original. */
const CORES = {
  preto:    { bg: '#3a3a3a', text: '#ffffff' },
  verde:    { bg: '#2E5E3A', text: '#ffffff' },
  vermelho: { bg: '#C62828', text: '#ffffff' },
  laranja:  { bg: '#E07A0B', text: '#ffffff' },
  roxo:     { bg: '#4A3F90', text: '#ffffff' },
  azul:     { bg: '#5E6CB8', text: '#ffffff' },
};

const plural = (n, s, p) => `${n} ${n === 1 ? s : p}`;
const iniciais = (nome) => nome.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

function coletarIds(node, acc = []) {
  acc.push(node.id);
  (node.filhos || []).forEach(f => coletarIds(f, acc));
  return acc;
}

// Pessoas visíveis num nó conforme o filtro de filial.
// 'todas' = mostra todo mundo (operacional é agrupado por filial na exibição).
// Filial específica = ESTRITO: só gente daquela filial, em qualquer nível (inclusive diretoria/gerência).
function pessoasVisiveis(node, filial) {
  if (filial === 'todas') return node.pessoas;
  return node.pessoas.filter(p => String(p.filial) === String(filial));
}

// Nó tem gente visível (nele ou em algum descendente)? Usado pra ocultar cargos vazios.
function temGente(node, filial) {
  if (pessoasVisiveis(node, filial).length > 0) return true;
  return (node.filhos || []).some(f => temGente(f, filial));
}

// Filhos que devem aparecer (têm gente neles ou abaixo).
function filhosVisiveis(node, filial) {
  return (node.filhos || []).filter(f => temGente(f, filial));
}

function Avatar({ pessoa, token, onAbrirFoto }) {
  const [erro, setErro] = useState(false);
  if (!pessoa.tem_foto || erro) {
    return <span className="org-avatar org-avatar--ini" aria-hidden="true">{iniciais(pessoa.nome)}</span>;
  }
  return (
    <img
      className="org-avatar org-avatar--foto"
      loading="lazy"
      alt=""
      title="Ver foto"
      src={`${API_URL}/organograma/foto/${pessoa.id}?t=${token}`}
      onError={() => setErro(true)}
      onClick={(e) => { e.stopPropagation(); onAbrirFoto && onAbrirFoto(pessoa); }}
    />
  );
}

function ListaPessoas({ pessoas, token, onAbrirFoto }) {
  return (
    <div className="org-pessoas-lista">
      {pessoas.map(p => (
        <div className="org-pessoa" key={p.id}>
          <Avatar pessoa={p} token={token} onAbrirFoto={onAbrirFoto} />
          <span className="org-pessoa-nome">{p.nome}</span>
        </div>
      ))}
    </div>
  );
}

function CorpoPessoas({ node, ctx }) {
  const pessoas = pessoasVisiveis(node, ctx.filial);
  if (pessoas.length === 0) {
    return <div className="org-corpo org-corpo--vazio">Ninguém nesta filial</div>;
  }
  // Operacional com "todas" as filiais → agrupa por filial
  if (node.tipo === 'operacional' && ctx.filial === 'todas') {
    const grupos = {};
    pessoas.forEach(p => { (grupos[p.filial] = grupos[p.filial] || []).push(p); });
    const filiais = Object.keys(grupos).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
    return (
      <div className="org-corpo">
        {filiais.map(f => (
          <div className="org-grupo" key={f}>
            <div className="org-grupo-label">Filial {f}</div>
            <ListaPessoas pessoas={grupos[f]} token={ctx.token} onAbrirFoto={ctx.onAbrirFoto} />
          </div>
        ))}
      </div>
    );
  }
  return <div className="org-corpo"><ListaPessoas pessoas={pessoas} token={ctx.token} onAbrirFoto={ctx.onAbrirFoto} /></div>;
}

function NodeCard({ node, expandido, expansivel, onToggle, ctx }) {
  const cores = CORES[node.cor] || { bg: '#888', text: '#fff' };
  const nPessoas = pessoasVisiveis(node, ctx.filial).length;
  const nAreas = filhosVisiveis(node, ctx.filial).length;

  return (
    <div
      className={`org-card${expansivel ? ' org-card--clicavel' : ''}`}
      onClick={expansivel ? onToggle : undefined}
      role={expansivel ? 'button' : undefined}
      tabIndex={expansivel ? 0 : undefined}
      onKeyDown={expansivel ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } } : undefined}
    >
      <div className="org-titulo" style={{ background: cores.bg, color: cores.text }}>
        <span className="org-titulo-txt">{node.titulo}</span>
        {expansivel && (
          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} org-chevron`} aria-hidden="true"></i>
        )}
      </div>

      {expandido && nPessoas > 0 && <CorpoPessoas node={node} ctx={ctx} />}

      {!expandido && expansivel && (
        <div className="org-meta">
          {nAreas > 0 && <span>{plural(nAreas, 'área', 'áreas')}</span>}
          {nAreas > 0 && nPessoas > 0 && <span className="org-meta-sep">·</span>}
          {nPessoas > 0 && <span>{plural(nPessoas, 'pessoa', 'pessoas')}</span>}
        </div>
      )}
    </div>
  );
}

function Subtree({ node, expandidos, onToggle, ctx }) {
  const expandido = expandidos.has(node.id);
  const visiveis = filhosVisiveis(node, ctx.filial);
  const temFilhos = visiveis.length > 0;
  const temPessoas = pessoasVisiveis(node, ctx.filial).length > 0;
  const expansivel = temFilhos || temPessoas;

  return (
    <div className="org-subtree">
      <NodeCard node={node} expandido={expandido} expansivel={expansivel} onToggle={() => onToggle(node.id)} ctx={ctx} />
      {expandido && temFilhos && (
        <div className="org-filhos">
          {visiveis.map(f => (
            <Subtree key={f.id} node={f} expandidos={expandidos} onToggle={onToggle} ctx={ctx} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Organograma() {
  const { token, temPermissao } = useAuth();
  const [raiz, setRaiz] = useState(null);
  const [naoClass, setNaoClass] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [expandidos, setExpandidos] = useState(() => new Set());
  const [filial, setFilial] = useState('todas');
  const [fotoAberta, setFotoAberta] = useState(null);

  // Fecha o modal de foto no Esc
  useEffect(() => {
    if (!fotoAberta) return;
    const onKey = (e) => { if (e.key === 'Escape') setFotoAberta(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fotoAberta]);

  useEffect(() => {
    api.get('/organograma')
      .then(({ data }) => {
        setRaiz(data.raiz);
        setNaoClass(data.nao_classificados || []);
        setFiliais(data.filiais || []);
        setExpandidos(new Set([data.raiz.id]));
      })
      .catch(err => setErro(err.response?.data?.error || 'Erro ao carregar organograma'))
      .finally(() => setCarregando(false));
  }, []);

  const todosIds = useMemo(() => (raiz ? coletarIds(raiz) : []), [raiz]);
  const ctx = { filial, token, onAbrirFoto: setFotoAberta };

  function toggle(id) {
    setExpandidos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  const expandirTudo = () => setExpandidos(new Set(todosIds));
  const recolherTudo = () => setExpandidos(raiz ? new Set([raiz.id]) : new Set());

  // Ao escolher uma filial, abre tudo (a árvore filtrada é pequena → vê de cara quem é).
  // Em "todas", volta a começar encolhido.
  function mudarFilial(nova) {
    setFilial(nova);
    if (nova === 'todas') setExpandidos(raiz ? new Set([raiz.id]) : new Set());
    else setExpandidos(new Set(todosIds));
  }

  const podeVerNaoClass = temPermissao('gerir_perfis') || temPermissao('gerir_colaboradores');
  const raizVisivel = raiz && temGente(raiz, filial);

  return (
    <>
      <Header />
      <main className="conteudo-container" style={{ maxWidth: 1400 }}>
        <div className="org-topo">
          <div>
            <h1 style={{ color: 'var(--verde)', fontWeight: 700, marginBottom: 8 }}>Organograma</h1>
            <p style={{ color: 'var(--texto-suave)', margin: 0 }}>
              Quem é quem no Centrão — atualizado automaticamente pelo RH. Toque num cargo pra abrir.
            </p>
          </div>
          {raiz && (
            <div className="org-acoes">
              {filiais.length > 0 && (
                <select className="org-filial-sel" value={filial} onChange={e => mudarFilial(e.target.value)}>
                  <option value="todas">Todas as filiais</option>
                  {filiais.map(f => <option key={f.codigo} value={f.codigo}>{f.nome}</option>)}
                </select>
              )}
              <div className="org-controles">
                <button type="button" className="org-btn" onClick={expandirTudo}>
                  <i className="bi bi-arrows-expand"></i> Expandir tudo
                </button>
                <button type="button" className="org-btn" onClick={recolherTudo}>
                  <i className="bi bi-arrows-collapse"></i> Recolher
                </button>
              </div>
            </div>
          )}
        </div>

        {carregando && <div>Carregando…</div>}
        {erro && <div className="erro">{erro}</div>}
        {raizVisivel && (
          <div className="org-wrapper">
            <Subtree node={raiz} expandidos={expandidos} onToggle={toggle} ctx={ctx} />
          </div>
        )}
        {raiz && !raizVisivel && !carregando && (
          <div className="conteudo-card" style={{ textAlign: 'center', color: 'var(--texto-suave)' }}>
            Ninguém ativo nesta filial.
          </div>
        )}

        {podeVerNaoClass && naoClass.length > 0 && (
          <div className="org-naoclass">
            <div className="org-naoclass-titulo">
              <i className="bi bi-exclamation-triangle"></i> Não classificados ({naoClass.length})
            </div>
            <p className="org-naoclass-sub">
              Funcionários ativos cujo cargo ainda não está mapeado pra uma posição. Defina onde encaixam.
            </p>
            <div className="org-pessoas-lista">
              {naoClass.map(p => (
                <div className="org-pessoa" key={p.id}>
                  <Avatar pessoa={p} token={token} onAbrirFoto={setFotoAberta} />
                  <span className="org-pessoa-nome">{p.nome}</span>
                  <span className="org-naoclass-cargo">{p.cargo || 'sem cargo'} · Filial {p.filial}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {fotoAberta && (
          <div className="org-foto-modal" onClick={() => setFotoAberta(null)}>
            <button className="org-foto-close" onClick={() => setFotoAberta(null)} aria-label="Fechar">×</button>
            <figure className="org-foto-fig" onClick={(e) => e.stopPropagation()}>
              <img src={`${API_URL}/organograma/foto/${fotoAberta.id}?t=${token}`} alt={fotoAberta.nome} />
              <figcaption>{fotoAberta.nome}</figcaption>
            </figure>
          </div>
        )}
      </main>

      <style>{`
        .org-topo {
          display: flex; justify-content: space-between; align-items: flex-end;
          flex-wrap: wrap; gap: 12px; margin-bottom: 16px;
        }
        .org-acoes { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .org-controles { display: flex; gap: 8px; }
        .org-filial-sel {
          padding: 7px 12px; border: 1px solid var(--borda); border-radius: 6px;
          font-size: 0.85rem; font-weight: 600; background: #fff; color: var(--texto); cursor: pointer;
        }
        .org-btn {
          display: inline-flex; align-items: center; gap: 6px; background: #fff;
          border: 1px solid var(--borda); color: var(--texto); padding: 7px 12px;
          border-radius: 6px; font-size: 0.82rem; font-weight: 600; cursor: pointer; white-space: nowrap;
        }
        .org-btn:hover { background: var(--bg); border-color: var(--verde); color: var(--verde); }

        .org-wrapper { overflow-x: auto; padding: 16px 4px 32px; }
        .org-subtree { display: flex; flex-direction: column; align-items: center; min-width: max-content; }
        .org-filhos {
          display: flex; gap: 16px; margin-top: 24px; position: relative;
          align-items: flex-start; animation: orgAbrir 0.18s ease;
        }
        @keyframes orgAbrir { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
        .org-filhos::before { content: ''; position: absolute; top: -12px; left: 50%; width: 2px; height: 12px; background: #ccc; }
        .org-card {
          background: #fff; border-radius: 8px; box-shadow: 0 2px 6px rgba(0,0,0,0.08);
          min-width: 200px; max-width: 260px; overflow: hidden; position: relative;
          transition: box-shadow 0.15s, transform 0.15s;
        }
        .org-card--clicavel { cursor: pointer; }
        .org-card--clicavel:hover { box-shadow: 0 6px 16px rgba(0,0,0,0.16); transform: translateY(-2px); }
        .org-card--clicavel:focus-visible { outline: 3px solid var(--verde-claro); outline-offset: 2px; }
        .org-card::before { content: ''; position: absolute; top: -12px; left: 50%; transform: translateX(-50%); width: 2px; height: 12px; background: #ccc; }
        .org-subtree:first-child > .org-card::before { display: none; }
        .org-titulo {
          padding: 10px 28px 10px 12px; font-weight: 700; font-size: 0.78rem;
          text-transform: uppercase; letter-spacing: 0.02em; position: relative;
          display: flex; align-items: center; justify-content: center; min-height: 40px;
        }
        .org-titulo-txt { text-align: center; }
        .org-chevron { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); font-size: 0.7rem; opacity: 0.85; }
        .org-meta { padding: 7px 12px; background: #f4f4f4; font-size: 0.72rem; color: #666; text-align: center; }
        .org-meta-sep { margin: 0 6px; color: #bbb; }

        .org-corpo { padding: 8px 12px; background: #f6f7f6; }
        .org-corpo--vazio { color: #999; font-size: 0.8rem; text-align: center; font-style: italic; }
        .org-grupo + .org-grupo { margin-top: 6px; }
        .org-grupo-label {
          font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.04em;
          color: #999; font-weight: 700; padding: 5px 0 3px; border-top: 1px solid #e7e7e7;
        }
        .org-grupo:first-child .org-grupo-label { border-top: none; padding-top: 0; }
        .org-pessoa { display: flex; align-items: center; gap: 8px; padding: 4px 0; }
        .org-avatar {
          width: 30px; height: 30px; border-radius: 50%; object-fit: cover; flex-shrink: 0;
          background: var(--verde-claro);
        }
        .org-avatar--ini {
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 0.68rem; font-weight: 700; color: var(--verde);
        }
        .org-pessoa-nome { font-size: 0.84rem; color: #333; text-align: left; }
        .org-avatar--foto { cursor: pointer; transition: opacity 0.15s, transform 0.15s; }
        .org-avatar--foto:hover { opacity: 0.85; transform: scale(1.08); }

        .org-foto-modal {
          position: fixed; inset: 0; background: rgba(0,0,0,0.78); z-index: 9999;
          display: flex; align-items: center; justify-content: center; cursor: pointer;
          padding: 20px; animation: orgFade 0.15s ease;
        }
        @keyframes orgFade { from { opacity: 0; } to { opacity: 1; } }
        .org-foto-fig { margin: 0; cursor: default; text-align: center; }
        .org-foto-fig img {
          max-width: 90vw; max-height: 80vh; border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.45); object-fit: contain; background: #fff;
        }
        .org-foto-fig figcaption { color: #fff; margin-top: 12px; font-size: 0.95rem; font-weight: 600; }
        .org-foto-close {
          position: fixed; top: 16px; right: 24px; background: rgba(0,0,0,0.5); color: #fff;
          border: none; border-radius: 50%; width: 40px; height: 40px; font-size: 1.5rem;
          cursor: pointer; display: flex; align-items: center; justify-content: center; line-height: 1;
        }
        .org-foto-close:hover { background: rgba(0,0,0,0.8); }

        .org-naoclass {
          margin-top: 28px; border: 1px dashed var(--borda); border-radius: 8px;
          padding: 16px; background: #fffdf7; max-width: 560px;
        }
        .org-naoclass-titulo { font-weight: 700; color: var(--laranja); display: flex; align-items: center; gap: 8px; }
        .org-naoclass-sub { font-size: 0.82rem; color: var(--texto-suave); margin: 6px 0 12px; }
        .org-naoclass-cargo { font-size: 0.74rem; color: #999; margin-left: auto; }

        @media (max-width: 600px) {
          .org-acoes { width: 100%; }
          .org-filial-sel { flex: 1; }
          .org-controles { flex: 2; }
          .org-btn { flex: 1; justify-content: center; }

          /* No celular vira árvore VERTICAL indentada — sem scroll lateral */
          .org-wrapper { overflow-x: visible; padding: 12px 0 28px; }
          .org-subtree { align-items: stretch; min-width: 0; width: 100%; }
          .org-card { max-width: none; min-width: 0; width: 100%; }
          .org-card::before { display: none; }
          .org-titulo { justify-content: flex-start; padding: 12px 32px 12px 14px; }
          .org-titulo-txt { text-align: left; }
          .org-meta { text-align: left; }

          .org-filhos {
            flex-direction: column; align-items: stretch; gap: 10px;
            margin: 10px 0 0 8px; padding-left: 16px; border-left: 2px solid #d2d2d2;
          }
          .org-filhos::before { display: none; }
          .org-filhos > .org-subtree { position: relative; }
          .org-filhos > .org-subtree::before {
            content: ''; position: absolute; left: -16px; top: 22px; width: 16px; height: 2px; background: #d2d2d2;
          }
        }
      `}</style>
    </>
  );
}
